import {
	faChevronDown,
	faChevronRight,
	faCompressAlt,
	faExpandAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { DebouncedInput } from "../../../../components/atoms/atom";
import { setExpandedGroups } from "../../../../store/slices/projectLVSlice";
import { formatQuantityToLocale, formatValueToLocale, showToast } from "../../../../utils/method";
import ElementDetailsModal from "./ElementDetailsModal";
import "./ServiceSpecificationsTable.scss";

const GROUP_TYPE = "ServiceSpecificationGroupDto";
const POSITION_TYPE = "PositionDto";
const NOTE_TYPE = "NoteTextDto";

const EMPTY_EXPANDED_ARRAY = Object.freeze([]);

const selectExpandedArrayForLV = (lvId) => (state) =>
	state.projectLV.expandedGroups[lvId] || EMPTY_EXPANDED_ARRAY;

// ── Pure helpers ─────────────────────────────────────────────────────────────
const getRowVariantClass = (element) => {
	if (element.elementType === GROUP_TYPE) return "element-group";
	if (element.isLot) return "element-lot";
	if (element.elementType === POSITION_TYPE) return "element-position";
	if (element.elementType === NOTE_TYPE) return "element-note";
	return "";
};

const getElementTypeLabel = (element, t) => {
	if (element.isLot) return t("lv.lot");
	if (element.elementType === GROUP_TYPE) return t("lv.group");
	if (element.elementType === POSITION_TYPE) return t("lv.position");
	if (element.elementType === NOTE_TYPE) return t("lv.note-text");
	return t("lv.unknown");
};

const ServiceSpecificationsTable = ({
	serviceSpecifications = [],
	lvPositions = [],
	isNachtraege = false,
	lvId = null,
	onPriceUpdate = null,
	// Defaults to true so existing modal callsites (read-only views without
	// price-edit anyway) keep their previous behaviour.
	canEditLV = true,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	// Per-LV expanded group IDs (array) live in Redux for cross-navigation persistence.
	// Read the array directly (stable reference between actions) rather than going
	// through the Set selector, which would allocate a new Set on every render.
	const expandedArray = useSelector(selectExpandedArrayForLV(lvId));

	const [selectedElement, setSelectedElement] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [savingPriceFor, setSavingPriceFor] = useState(null);
	// Stored in a ref (not state): we only read this on blur to decide whether
	// the price actually changed. Putting it in state would re-render the parent
	// on every focus, which forces the `columns` memo to recompute and (because
	// TanStack's flexRender wraps the cell fn in React.createElement) unmounts
	// the input - killing focus the moment the user clicks it.
	const originalPricesRef = useRef({});

	// ── Redux ⇄ TanStack expansion bridge ────────────────────────────────────
	// TanStack expects expansion as `Record<rowId, boolean>`. Build that view from
	// the Redux array; the memo only recomputes when the array reference changes.
	const expandedState = useMemo(() => {
		const out = {};
		expandedArray.forEach((id) => {
			out[id] = true;
		});
		return out;
	}, [expandedArray]);

	const handleExpandedChange = useCallback(
		(updater) => {
			if (!lvId) return;
			const next =
				typeof updater === "function" ? updater(expandedState) : updater;
			if (next === true) {
				// TanStack uses literal `true` to mean "expand every expandable row".
				// Walk the tree once and persist all group IDs.
				const allIds = [];
				const collect = (nodes) => {
					nodes.forEach((node) => {
						if (node.elementType === GROUP_TYPE) {
							allIds.push(node.id);
							if (node.elements?.length) collect(node.elements);
						}
					});
				};
				(serviceSpecifications || []).forEach((spec) => {
					if (spec.elements?.length) collect(spec.elements);
				});
				dispatch(setExpandedGroups({ lvId, groupIds: allIds }));
				return;
			}
			const ids = Object.keys(next).filter((k) => next[k]);
			dispatch(setExpandedGroups({ lvId, groupIds: ids }));
		},
		[dispatch, lvId, expandedState, serviceSpecifications],
	);

	// ── Cost-group lookup (by gaebPositionId → costGroupLabel) ───────────────
	const costGroupByGaebId = useMemo(() => {
		const map = new Map();
		(lvPositions || []).forEach((pos) => {
			if (pos?.gaebPositionId) {
				map.set(pos.gaebPositionId, pos.costGroupLabel);
			}
		});
		return map;
	}, [lvPositions]);

	const getLvPositionId = useCallback(
		(element) => {
			if (!Array.isArray(lvPositions) || lvPositions.length === 0) return null;
			const lvPosition = lvPositions.find(
				(pos) => pos.gaebPositionId === element.id,
			);
			return lvPosition?.id || null;
		},
		[lvPositions],
	);

	// ── Price-edit handlers ──────────────────────────────────────────────────
	const handlePriceKeyDown = useCallback((event) => {
		if (event.key === "Tab") {
			event.preventDefault();
			event.target.blur(); // trigger save without navigating to next row's input
		}
	}, []);

	const handlePriceFocus = useCallback((element) => {
		originalPricesRef.current[element.id] = element.unitPrice;
	}, []);

	const handlePriceBlur = useCallback(
		async (element, value) => {
			if (!element || !value || value.trim() === "") return;

			const newPrice = parseFloat(value);
			if (isNaN(newPrice)) {
				showToast(t("lv.error-invalid-price"), { type: "error" });
				return;
			}

			const originalPrice = originalPricesRef.current[element.id];
			if (
				originalPrice !== undefined &&
				parseFloat(originalPrice) === newPrice
			) {
				return; // unchanged → no backend call
			}

			if (!lvId) {
				showToast(t("lv.error-no-lv-id"), { type: "error" });
				return;
			}

			const positionId = getLvPositionId(element);
			if (!positionId) {
				showToast(t("lv.error-position-not-found"), { type: "error" });
				return;
			}

			try {
				setSavingPriceFor(element.id);
				if (onPriceUpdate) {
					await onPriceUpdate(positionId, newPrice);
				}
			} catch (error) {
				console.error("Error saving price:", error);
				showToast(error.message || t("lv.error-saving-price"), { type: "error" });
			} finally {
				setSavingPriceFor(null);
			}
		},
		[lvId, onPriceUpdate, t, getLvPositionId],
	);

	// ── Row-click → details modal (skipped for group rows; cells with their
	//    own onClick e.stopPropagation() opt out, e.g. the price input) ──────
	const handleRowClick = useCallback((element, event) => {
		if (event.defaultPrevented) return;
		setSelectedElement(element);
		setShowModal(true);
	}, []);

	// ── Column definitions ───────────────────────────────────────────────────
	const columns = useMemo(
		() => [
			{
				id: "type",
				header: t("type"),
				meta: { thClassName: "sst-col--15", tdClassName: "sst-col--15" },
				cell: ({ row }) => {
					const element = row.original;
					const label = getElementTypeLabel(element, t);
					const number = element?.itemNumber?.stringRepresentation || "";

					if (element.elementType === GROUP_TYPE) {
						return (
							<div
								className={`d-flex align-items-center sst-indent--${Math.min(row.depth, 4)}`}
							>
								<FontAwesomeIcon
									icon={row.getIsExpanded() ? faChevronDown : faChevronRight}
									className="me-2"
								/>
								{label} {number}
							</div>
						);
					}
					return `${label} ${number}`;
				},
			},
			{
				id: "costGroup",
				header: t("lv.cost-group-assignment.cost-group"),
				meta: { thClassName: "sst-col--15" },
				cell: ({ row }) => {
					const element = row.original;
					if (element.elementType === GROUP_TYPE) return null;
					if (element.elementType !== POSITION_TYPE) return null;

					const costGroupLabel = costGroupByGaebId.get(element.id);
					const missing = !costGroupLabel;
					return (
						<span className={missing ? "text-danger" : undefined}>
							{costGroupLabel || t("lv.cost-group-assignment.not-assigned")}
						</span>
					);
				},
			},
			{
				id: "shortText",
				header: t("lv.short-text"),
				meta: {
					thClassName: "sst-col--35",
					getTdClassName: (row) =>
						row.original.elementType === GROUP_TYPE ? "sst-col--35" : "sst-col--desc",
				},
				cell: ({ row }) => {
					const element = row.original;
					if (element.elementType === GROUP_TYPE) {
						return element?.shortText || "";
					}
					return element?.shortText || t("invoice.no-description");
				},
			},
			{
				id: "quantity",
				header: t("lv.quantity"),
				meta: { thClassName: "text-right sst-col--15", tdClassName: "text-right sst-col--15" },
				cell: ({ row }) => {
					const element = row.original;
					if (element.elementType === GROUP_TYPE) return null;
					return element?.quantity
						? `${formatQuantityToLocale(element.quantity)} ${element.unitTag || ""}`
						: "";
				},
			},
			{
				id: "unitPrice",
				header: t("lv.unit-price"),
				meta: {
					thClassName: "text-right sst-col--12",
					tdClassName: "text-right sst-col--12",
					// Always swallow clicks on this cell so neither editing the price
					// nor reading it triggers the row-level details modal.
					tdOnClick: (event) => event.stopPropagation(),
				},
				cell: ({ row }) => {
					const element = row.original;
					if (element.elementType === GROUP_TYPE) return null;

					if (isNachtraege && element.elementType === POSITION_TYPE) {
						const isSaving = savingPriceFor === element.id;
						const isLocked = !canEditLV;
						return (
							<div
								className={`sst-price-input d-flex align-items-center justify-content-end position-relative${isLocked ? " sst-price-input--locked" : ""}`}
								title={
									isLocked
										? t("lv.error-no-edit-permission")
										: undefined
								}
							>
								<DebouncedInput
									value={element?.unitPrice?.toString() || "0"}
									onFocus={
										isLocked
											? undefined
											: () => handlePriceFocus(element)
									}
									onBlur={isLocked ? undefined : handlePriceBlur}
									onKeyDown={
										isLocked ? undefined : handlePriceKeyDown
									}
									position={element}
									placeholder="0,00"
									decimalPlaces={2}
									disabled={isLocked || isSaving}
									suffix="€"
								/>
								{isSaving && (
									<div
										className="spinner-border spinner-border-sm position-absolute sst-spinner-overlay"
										role="status"
									>
										<span className="visually-hidden">Loading...</span>
									</div>
								)}
							</div>
						);
					}
					return <span>{formatValueToLocale(element?.unitPrice, true)}</span>;
				},
			},
			{
				id: "totalPrice",
				header: t("lv.net"),
				meta: { thClassName: "text-right sst-col--11", tdClassName: "text-right sst-col--11" },
				cell: ({ row }) => formatValueToLocale(row.original?.totalPrice, true),
			},
			{
				id: "totalPriceGross",
				header: t("lv.gross-after-discount"),
				meta: { thClassName: "text-right sst-col--12", tdClassName: "text-right sst-col--12" },
				cell: ({ row }) => formatValueToLocale(row.original?.totalPriceGross, true),
			},
		],
		[
			t,
			costGroupByGaebId,
			isNachtraege,
			canEditLV,
			savingPriceFor,
			handlePriceFocus,
			handlePriceBlur,
			handlePriceKeyDown,
		],
	);

	// Flatten the spec containers - only their `.elements` are rendered as rows
	// (matching the original component's behaviour). The spec wrappers themselves
	// aren't part of the visible hierarchy.
	const tableData = useMemo(
		() => (serviceSpecifications || []).flatMap((spec) => spec?.elements || []),
		[serviceSpecifications],
	);

	// ── Table instance ───────────────────────────────────────────────────────
	const table = useReactTable({
		data: tableData,
		columns,
		getRowId: (row) => row.id,
		getSubRows: (row) =>
			row.elementType === GROUP_TYPE ? row.elements || [] : undefined,
		state: { expanded: expandedState },
		onExpandedChange: handleExpandedChange,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		// Data refetches (e.g. after a price save) shouldn't collapse the user's
		// open groups.
		autoResetExpanded: false,
	});

	const expandAllGroups = useCallback(() => {
		table.toggleAllRowsExpanded(true);
	}, [table]);

	const collapseAllGroups = useCallback(() => {
		table.toggleAllRowsExpanded(false);
	}, [table]);

	return (
		<>
			<section className="sst-section">
				<header className="sst-section-header">
					<h2 className="sst-section-title">{t("lv.service-specs")}</h2>
					<div className="sst-section-actions">
						<button
							className="btn btn-sm btn-outline-primary me-2"
							onClick={expandAllGroups}
						>
							<FontAwesomeIcon icon={faExpandAlt} className="me-1" />
							{t("lv.expand-all")}
						</button>
						<button
							className="btn btn-sm btn-outline-secondary"
							onClick={collapseAllGroups}
						>
							<FontAwesomeIcon icon={faCompressAlt} className="me-1" />
							{t("lv.collapse-all")}
						</button>
					</div>
				</header>

				<div className="sst-scroll-body">
					<table className="table service-spec-table">
						<thead>
							<tr>
								{table.getHeaderGroups()[0].headers.map((header) => (
									<th
										key={header.id}
										className={header.column.columnDef.meta?.thClassName}
									>
										{flexRender(
											header.column.columnDef.header,
											header.getContext(),
										)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{table.getRowModel().rows.map((row) => {
								const element = row.original;
								const isGroup = element.elementType === GROUP_TYPE;
								const variantClass = getRowVariantClass(element);
								const levelClass =
									isGroup && row.depth > 0
										? ` element-group--level-${Math.min(row.depth, 3)}`
										: "";
								const nestedClass =
									isGroup && row.depth > 0 ? " nested-group" : "";
								const clickableClass = isGroup ? "" : " clickable-row";

								const handleClick = isGroup
									? row.getToggleExpandedHandler()
									: (e) => handleRowClick(element, e);

								return (
								<tr
									key={row.id}
									className={`${variantClass}${levelClass}${nestedClass}${clickableClass}`}
									onClick={handleClick}
								>
									{row.getVisibleCells().map((cell) => {
										// Groups never have a cost-group value, so skip that cell
										// and let the Type cell absorb its width via colSpan=2.
										if (isGroup && cell.column.id === "costGroup") {
											return null;
										}
										const meta = cell.column.columnDef.meta || {};
										const tdClassName =
											(meta.getTdClassName && meta.getTdClassName(row)) ||
											meta.tdClassName;
										const tdOnClick = meta.tdOnClick
											? (e) => meta.tdOnClick(e, row)
											: undefined;
										const colSpan =
											isGroup && cell.column.id === "type" ? 2 : undefined;
										return (
											<td
												key={cell.id}
												className={tdClassName}
												onClick={tdOnClick}
												colSpan={colSpan}
											>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext(),
												)}
											</td>
										);
									})}
								</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</section>

			<ElementDetailsModal
				show={showModal}
				element={selectedElement}
				onHide={() => {
					setShowModal(false);
					setSelectedElement(null);
				}}
			/>
		</>
	);
};

export default ServiceSpecificationsTable;
